import React from 'react'
import styled from 'styled-components'
import { zoning_indice_sources } from '@popety-io/types'
import {
  isEnvelopeScored,
  getEnvelopeSource,
  getEnvelopeState,
  getEnvelopeVariant,
} from '@popety-io/utils'
import { MetricSourceBadge } from '../../../atoms/MetricSourceBadge/MetricSourceBadge'
import { Icon } from '../../../atoms/Icon'
import { VisibilityToggle } from './VisibilityToggle'
import {
  MetricLabel,
  MetricValueCell,
  EmptyValue,
  AuthorizedValue,
  AuthorizedValueWithBadge,
} from './ZoneMetricsTable.styles'
import { DerivedSurfaceRow } from './DerivedSurfaceRow'
import { MetricProgressBarTranslations } from './MetricProgressBar'
import { ZoningLandData } from '../hooks/useZoningMetrics'

const AnchorRow = styled.tr`
  td {
    padding: ${({ theme }) =>
      `${theme.spacing.small} ${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.medium}`};
    border-bottom: none;
  }
`

const AnchorLabelWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
`

const AnchorSubNote = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

const MethodCellText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const FullWidthCell = styled.td`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: ${({ theme }) =>
    `${theme.spacing.xsmall} ${theme.spacing.medium} ${theme.spacing.small} ${theme.spacing.medium}`};
`

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

const CaveatChip = styled.span<{ $tone?: 'amber' | 'red' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  background: ${({ theme }) => theme.colors.background.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  color: ${({ $tone, theme }) => {
    switch ($tone) {
      case 'red':
        return theme.colors.danger
      case 'info':
        return theme.colors.primary
      default:
        return '#cc6b00'
    }
  }};
`

const LiveChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  background: ${({ theme }) => theme.colors.successLight};
  color: #008a2e;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  padding: ${({ theme }) => `2px ${theme.spacing.small}`};
`

const LockNote = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export interface EnvelopeSectionTranslations {
  anchorLabel?: string
  anchorSub?: string
  authorisedCell?: string
  buildableFootprint?: string
  maxBuildingVolume?: string
  caveatMinSetback?: string
  caveatRoofAllowance?: string
  capNote?: string
  contiguousChip?: string
  unbuildableBanner?: string
  lockedNote?: string
  recomputedFromHeight?: string
  previewFromSetback?: string
}

export const DEFAULT_ENVELOPE_TRANSLATIONS: EnvelopeSectionTranslations = {
  anchorLabel: 'Standard construction rules (Regelbauweise)',
  anchorSub:
    'This zone has no utilization ratio — potential is defined by setbacks & height',
  authorisedCell: 'Setbacks × Height',
  buildableFootprint: 'Buildable footprint',
  maxBuildingVolume: 'Maximum building volume',
  caveatMinSetback:
    'Based on the minimum boundary setback only — the real potential may be lower',
  caveatRoofAllowance: 'Height includes a standard roof allowance',
  capNote:
    'Max building length/depth limits the envelope: {setback} m² → {effective} m²',
  contiguousChip:
    'Contiguous construction (ordre contigu) — setbacks apply to non-attached boundaries only',
  unbuildableBanner:
    'Setbacks leave no buildable footprint on this plot alone — possible via parcel assembly (Näherbaurecht)',
  lockedNote: 'Computed — adjust the height and setbacks below',
  recomputedFromHeight: 'recomputed from your height',
  previewFromSetback: 'preview from your setback',
}

/** Live override values computed by the table in edit mode. */
export interface EnvelopeLiveValues {
  /** Recomputed authorized volume (m³) when the height is overridden */
  authorizedVolume?: number
  /** Recomputed footprint (m²) from the turf setback preview */
  footprintM2?: number
  reason?: 'height' | 'setback'
}

export interface EnvelopeSectionProps {
  landData: ZoningLandData
  isEditing: boolean
  live?: EnvelopeLiveValues
  translations?: EnvelopeSectionTranslations
  progressBarTranslations?: MetricProgressBarTranslations
  /** Opens the envelope calculation modal (anchor badge / value click) */
  onAnchorClick?: () => void
  /** Visibility controls (edit mode) — hiding is display-only */
  showVisibilityToggle?: boolean
  isHidden?: boolean
  onToggleVisibility?: () => void
  hideTooltip?: string
  showTooltip?: string
}

const interpolate = (template: string, vars: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`)

/**
 * Regelbauweise envelope block (approved design: Idea B, Variant 1).
 * Renders at indice level in ZoneMetricsTable when `indice_used` is
 * `envelope_*`: an anchor row + two parcel-derived rows, state banners and
 * precision caveats. Replaces the estimated ratio rows in view mode.
 */
export const EnvelopeSection: React.FC<EnvelopeSectionProps> = ({
  landData,
  isEditing,
  live,
  translations,
  progressBarTranslations,
  onAnchorClick,
  showVisibilityToggle = false,
  isHidden = false,
  onToggleVisibility,
  hideTooltip,
  showTooltip,
}) => {
  const t = { ...DEFAULT_ENVELOPE_TRANSLATIONS, ...translations }

  if (!isEnvelopeScored(landData.indice_used)) return null

  const source = (getEnvelopeSource(landData.indice_used) ||
    'llm') as zoning_indice_sources
  const state = getEnvelopeState(landData)
  if (state === null) return null

  const vbr = landData.vbr ?? undefined
  const baseVolume = landData.volume_envelope_authorized ?? undefined
  const baseFootprint =
    landData.buildable_area_effective ??
    landData.buildable_area_setback ??
    undefined

  const liveVolume = isEditing ? live?.authorizedVolume : undefined
  const liveFootprint = isEditing ? live?.footprintM2 : undefined
  const authorizedVolume = liveVolume ?? baseVolume
  const footprint = liveFootprint ?? baseFootprint

  const isCapped =
    landData.building_length_capped === true ||
    landData.building_depth_capped === true
  const showCapNote =
    isCapped &&
    landData.buildable_area_setback != null &&
    landData.buildable_area_effective != null &&
    landData.buildable_area_setback !== landData.buildable_area_effective

  // All state/precision notes render as compact chips — same visual weight
  // as the rest of the table, no banner chrome.
  const chips: { text: string; tone: 'amber' | 'red' | 'info' }[] = []
  if (
    getEnvelopeVariant(landData.indice_used) === 'contiguous' &&
    t.contiguousChip
  ) {
    chips.push({ text: t.contiguousChip, tone: 'info' })
  }
  if (state === 'unbuildable_alone' && t.unbuildableBanner) {
    chips.push({ text: t.unbuildableBanner, tone: 'info' })
  }
  if (showCapNote && t.capNote) {
    chips.push({
      text: interpolate(t.capNote, {
        setback: String(landData.buildable_area_setback),
        effective: String(landData.buildable_area_effective),
      }),
      tone: 'amber',
    })
  }
  if (landData.setback_differentiated === false && t.caveatMinSetback) {
    chips.push({ text: t.caveatMinSetback, tone: 'amber' })
  }
  if (
    landData.envelope_height_used === 'without_roof_plus_allowance' &&
    t.caveatRoofAllowance
  ) {
    chips.push({ text: t.caveatRoofAllowance, tone: 'amber' })
  }

  return (
    <>
      {/* Anchor row — the indice-level entry for the envelope method */}
      <AnchorRow style={isHidden ? { opacity: 0.45 } : undefined}>
        <td>
          <AnchorLabelWrap>
            {showVisibilityToggle && onToggleVisibility && (
              <VisibilityToggle
                isHidden={isHidden}
                onToggle={onToggleVisibility}
                hideTooltip={hideTooltip}
                showTooltip={showTooltip}
              />
            )}
            <div>
              <MetricLabel>{t.anchorLabel}</MetricLabel>
              <AnchorSubNote>
                {isEditing ? <LockNote>{t.lockedNote}</LockNote> : t.anchorSub}
              </AnchorSubNote>
            </div>
          </AnchorLabelWrap>
        </td>
        <MetricValueCell>
          <EmptyValue>-</EmptyValue>
        </MetricValueCell>
        <MetricValueCell>
          <AuthorizedValueWithBadge>
            <AuthorizedValue
              $source={source}
              onClick={onAnchorClick}
              style={{ cursor: onAnchorClick ? 'pointer' : undefined }}
              title="Click to view how this is calculated"
            >
              <MethodCellText>{t.authorisedCell}</MethodCellText>
            </AuthorizedValue>
            <span
              onClick={onAnchorClick}
              style={{ cursor: onAnchorClick ? 'pointer' : undefined }}
            >
              <MetricSourceBadge source={source} />
            </span>
          </AuthorizedValueWithBadge>
        </MetricValueCell>
        <MetricValueCell>
          <EmptyValue>-</EmptyValue>
        </MetricValueCell>
      </AnchorRow>

      {/* Parcel-derived rows (same grammar as SBd/SPd under a ratio);
          collapsed while the block is hidden in edit mode */}
      {!isHidden && (
        <DerivedSurfaceRow
          title={t.buildableFootprint || ''}
          authorizedValue={footprint}
          unit="m²"
          progressBarTranslations={progressBarTranslations}
        />
      )}
      {!isHidden && (
        <DerivedSurfaceRow
          title={t.maxBuildingVolume || ''}
          currentValue={vbr}
          authorizedValue={authorizedVolume}
          unit="m³"
          progressBarTranslations={progressBarTranslations}
        />
      )}

      {/* Live recompute chip (edit mode) */}
      {!isHidden && isEditing && live?.reason && (
        <tr>
          <FullWidthCell colSpan={4}>
            <LiveChip>
              <Icon name="refresh-cw" size="small" />
              {live.reason === 'height'
                ? t.recomputedFromHeight
                : t.previewFromSetback}
            </LiveChip>
          </FullWidthCell>
        </tr>
      )}

      {/* State + precision notes as compact chips */}
      {!isHidden && chips.length > 0 && (
        <tr>
          <FullWidthCell colSpan={4}>
            <ChipsRow>
              {chips.map((chip) => (
                <CaveatChip key={chip.text} $tone={chip.tone}>
                  <Icon
                    name={chip.tone === 'amber' ? 'alert-triangle' : 'info'}
                    size="small"
                  />
                  {chip.text}
                </CaveatChip>
              ))}
            </ChipsRow>
          </FullWidthCell>
        </tr>
      )}
    </>
  )
}
